import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  NameSanitizationRule,
  PreviewNameSanitizationResponse,
  RunNameCleaningResponse,
} from '@shared/schemas';

import { initialState } from './name-rules.state';

/**
 * Name rules slice. Reducers are pure state transitions only; every fetch and
 * write lives in the saga, which dispatches these as it progresses.
 */
const slice = createSlice({
  name: 'nameRules',
  initialState,
  reducers: {
    // ── Blocklist load ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<NameSanitizationRule[]>) => {
      state.rules = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Add / remove ──
    mutationStarted: (state) => {
      state.mutation = { status: 'pending', error: null };
    },
    ruleCreated: (state, action: PayloadAction<NameSanitizationRule>) => {
      state.rules.push(action.payload);
      state.mutation = { status: 'idle', error: null };
      // The blocklist just changed, so a preview computed under the old rules is
      // stale and would quietly contradict the list next to it.
      state.preview = null;
    },
    ruleDeleted: (state, action: PayloadAction<string>) => {
      state.rules = state.rules.filter((r) => r.id !== action.payload);
      state.mutation = { status: 'idle', error: null };
      state.preview = null;
    },
    rulesCleared: (state, action: PayloadAction<NameSanitizationRule['kind']>) => {
      state.rules = state.rules.filter((r) => r.kind !== action.payload);
      state.mutation = { status: 'idle', error: null };
      state.preview = null;
    },
    mutationFailed: (state, action: PayloadAction<string>) => {
      state.mutation = { status: 'failed', error: action.payload };
    },
    mutationCleared: (state) => {
      state.mutation = { status: 'idle', error: null };
    },

    // ── Test field ──
    previewLoaded: (state, action: PayloadAction<PreviewNameSanitizationResponse>) => {
      state.preview = action.payload;
    },
    previewCleared: (state) => {
      state.preview = null;
    },

    // ── Retroactive pass ──
    runStarted: (state) => {
      state.running = true;
      state.mutation = { status: 'pending', error: null };
    },
    runSucceeded: (state, action: PayloadAction<RunNameCleaningResponse>) => {
      state.running = false;
      state.lastRun = action.payload;
      state.mutation = { status: 'idle', error: null };
    },
    runFailed: (state, action: PayloadAction<string>) => {
      state.running = false;
      state.mutation = { status: 'failed', error: action.payload };
    },

    /** Reset everything (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const nameRulesActions = slice.actions;
export const nameRulesReducer = slice.reducer;
